/**
 * The browser's own calendar day.
 *
 * Every function here reads the device clock and the device timezone through
 * `Date`'s *local* getters. There is no timezone name, no offset, no
 * configuration value and no `moment-timezone` call anywhere in this module: a
 * machine set to Tokyo rolls over at Tokyo midnight, one set to Tel Aviv at Tel
 * Aviv midnight, and changing the OS timezone moves the rollover with it.
 */

/**
 * `YYYY-MM-DD` for the device's local calendar day.
 *
 * Built from `getFullYear` / `getMonth` / `getDate` rather than
 * `toISOString()`, which is UTC and would therefore roll the day over at the
 * wrong local moment for everyone outside UTC — 03:00 for a machine set to
 * Israel in summer, 09:00 for one set to Tokyo.
 */
export const localDayKey = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Local midnight that opened this date's calendar day.
 *
 * In zones that spring forward *at* midnight (Santiago, Havana) local 00:00
 * does not exist on that date and the platform clamps to 01:00 — still the
 * correct start of that day, so callers must not assert `getHours() === 0`.
 */
export const startOfLocalDay = (date: Date = new Date()): Date => {
  const start = new Date(date.getTime());
  start.setHours(0, 0, 0, 0);
  return start;
};

/**
 * How long the device's clock has been inside the current calendar day.
 *
 * Derived by subtracting the two instants rather than by arithmetic on a
 * 24-hour constant, because a DST day is 23 or 25 hours long.
 */
export const msSinceLocalMidnight = (date: Date = new Date()): number =>
  date.getTime() - startOfLocalDay(date).getTime();
